import { useMemo, useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Box } from '@givebutter/primitives/box';
import { Label } from '@givebutter/primitives/label';
import { useToast } from '@givebutter/primitives/toast';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faEye } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    deleteCampaignExpertById,
    fetchCampaignExperts,
    linkExpertToCampaign,
} from 'api/campaigns';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import { ListItem } from 'components/list-item';
import * as Select from 'components/select';
import { SelectItemWithLabel } from 'components/select';
import * as SettingsCard from 'components/settings-card';
import * as Table from 'components/table';
import { CAMPAIGN_EXPERTS_LINK } from 'constants/links';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignExpert } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<CampaignExpert>();
const availableExpertParams = { isAvailable: true };

const CampaignExpertsSettings = () => {
    const toast = useToast();
    const apiUrl = useAppState(state => state.apiUrl);
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;
    const [count, setCount] = useState(0);

    const { data: linkedExperts } = useSuspenseQuery({
        queryKey: campaignQueryKeys.experts(campaignId),
        queryFn: () => fetchCampaignExperts(campaignId),
    });

    const { data: availableExperts } = useSuspenseQuery({
        queryKey: campaignQueryKeys.expertsWithFilters(campaignId, availableExpertParams),
        queryFn: () => fetchCampaignExperts(campaignId, availableExpertParams),
    });

    const { mutate: linkExpert } = useMutation({
        mutationFn: (expertId: number) => linkExpertToCampaign(campaignId, expertId),
        onSuccess: () => {
            toast.success('Linked Givebutter Expert successfully!');
            return queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.experts(campaignId),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to link Givebutter Expert'),
        onSettled: () => {
            setCount(c => c + 1);
        },
    });

    const { mutate: deleteExpert } = useMutation({
        mutationFn: (expertId: number) => deleteCampaignExpertById(campaignId, expertId),
        onSuccess: () => {
            toast.success('Removed Givebutter Expert successfully!');
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.experts(campaignId) });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to remove Givebutter Expert'),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('first_name', {
                header: 'Name',
                cell: info => {
                    const expert = info.row.original;

                    return (
                        <p className={'font-medium'}>
                            {expert.first_name} {expert.last_name}
                        </p>
                    );
                },
            }),
            columnHelper.accessor('company', {
                header: 'Company',
                cell: info => info.getValue(),
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const expert = info.row.original;

                    return (
                        <DropdownMenu.Root key={expert.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content
                                onSelect={e => e.preventDefault()}
                                sideOffset={5}
                                align={'end'}
                            >
                                <DropdownMenu.Item disabled={!expert.slug} asChild={true}>
                                    <a
                                        className={
                                            'radix-disabled:pointer-events-none flex items-center gap-2 text-black'
                                        }
                                        href={`${apiUrl}/experts/${expert.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FontAwesomeIcon icon={faEye} />
                                        <p>View</p>
                                    </a>
                                </DropdownMenu.Item>
                                <AlertDialog.Root type={'error'}>
                                    <AlertDialog.Trigger asChild={true}>
                                        <DropdownMenu.Item
                                            className={'flex items-center gap-2'}
                                            onSelect={e => e.preventDefault()}
                                        >
                                            <FontAwesomeIcon
                                                className={'text-red-600'}
                                                icon={faTrash}
                                            />
                                            <p className={'text-red-600'}>Delete</p>
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Delete Givebutter Expert from this campaign
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    You will not be able to undo this action.
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() => deleteExpert(expert.id)}
                                                >
                                                    Delete
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [apiUrl, deleteExpert],
    );

    const table = useReactTable({
        data: linkedExperts,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>Givebutter Experts</SettingsCard.Heading>
                <p className={'text-sm text-gray-700'}>
                    Enhance your campaigns and raise more with the help of our trusted consultants,
                    freelancers, and agencies.{' '}
                    <a href={CAMPAIGN_EXPERTS_LINK} target={'_blank'} rel={'noopener noreferrer'}>
                        Learn more
                    </a>
                    .
                </p>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                <div className={'flex flex-col gap-4'}>
                    <Form.Container>
                        <Label className={'text-base'} htmlFor={'expert-search-input'}>
                            Link Givebutter Experts
                        </Label>
                        <p className={'text-sm text-gray-700'}>
                            Type the name of the Givebutter Experts who are helping with this
                            campaign.
                        </p>
                        <Select.SearchableSelect
                            key={count}
                            multi={false}
                            onSelectItem={(expert: SelectItemWithLabel | null) => {
                                if (expert) {
                                    linkExpert(parseInt(expert.id));
                                }
                            }}
                            inputProps={{
                                placeholder: 'Search by name',
                                id: 'expert-search-input',
                            }}
                        >
                            {availableExperts.length === 0 && (
                                <ListItem>No linkable experts found.</ListItem>
                            )}
                            {availableExperts.map(expert => {
                                const expertName = `${expert.first_name} ${expert.last_name}`;

                                return (
                                    <Select.ListItem
                                        key={expert.id}
                                        value={{ id: expert.id.toString(), label: expertName }}
                                    >
                                        {expertName}
                                    </Select.ListItem>
                                );
                            })}
                        </Select.SearchableSelect>
                    </Form.Container>
                    <Box className="overflow-hidden">
                        <Table.Root variant={'bordered'} table={table}>
                            <Table.Content>
                                <Table.Header />
                                <Table.Body />
                            </Table.Content>
                            <Table.Empty variant={'secondary'}>
                                <Table.EmptyIcon />
                                <Table.EmptyTitle>No linked experts yet</Table.EmptyTitle>
                            </Table.Empty>
                        </Table.Root>
                    </Box>
                </div>
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { CampaignExpertsSettings };
